import { createUser, Permission, User } from "@/domain/user"
import { createContext, useContext } from "react"

export const UserContext = createContext<{
  user: User
  signOut: () => void
  setCurrentScope: (scopeId: string | null) => void
  updateFeatureFlags: (featureFlags: Partial<Features.Flags>) => void
  hasPermission: (permission: Permission) => boolean | undefined
  changeLanguage: (language: Settings.Language) => void
}>({
  user: createUser(),
  signOut: () => {},
  setCurrentScope: () => {},
  updateFeatureFlags: () => {},
  hasPermission: () => true,
  changeLanguage: () => {}
})

export const useUser = () => {
  const user = useContext(UserContext)

  return user
}

type FeatureFlagsKeys = keyof Features.Flags
type SettingsKeys = keyof Settings.Shared

function useUserProperty<T>(propertyName: keyof User, key?: keyof T) {
  const { user } = useUser()
  const property = user[propertyName] as T

  if (!key) {
    return property
  }

  return property[key]
}

export function useFeatureFlags<K extends FeatureFlagsKeys>(
  name: K
): Features.Flags[K]
export function useFeatureFlags(): Features.Flags
export function useFeatureFlags(name?: FeatureFlagsKeys) {
  return useUserProperty<Features.Flags>("featureFlags", name)
}

export function useSettings<K extends SettingsKeys>(name: K): Settings.Shared[K]
export function useSettings(): Settings.Shared
export function useSettings(name?: SettingsKeys) {
  return useUserProperty<Settings.Shared>("settings", name)
}
